import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Bookmark, Download, Filter, Search, SlidersHorizontal } from 'lucide-react';
import { formatCrores, formatInr, formatVolume } from '../utils/market.js';

export default function Screener({ stocks, onSelectStock, watchlist, onToggleWatchlist, title = 'Stock screener', description = 'Filter observable market and fundamental data. Results are not recommendations.' }) {
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('All sectors');
  const [movement, setMovement] = useState('All moves');
  const [sort, setSort] = useState({ key: 'marketCap', direction: 'desc' });

  const sectors = ['All sectors', ...new Set(stocks.map((stock) => stock.sector))];
  const filtered = useMemo(() => {
    const result = stocks.filter((stock) => {
      const matchesQuery = `${stock.symbol} ${stock.name} ${stock.industry}`.toLowerCase().includes(query.toLowerCase());
      const matchesSector = sector === 'All sectors' || stock.sector === sector;
      const matchesMovement = movement === 'All moves' || (movement === 'Gainers' ? stock.change > 0 : stock.change < 0);
      return matchesQuery && matchesSector && matchesMovement;
    });
    return result.sort((a, b) => {
      const first = a[sort.key];
      const second = b[sort.key];
      return (first > second ? 1 : first < second ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [stocks, query, sector, movement, sort]);

  const changeSort = (key) => {
    setSort((current) => ({ key, direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc' }));
  };

  const SortIcon = ({ field }) => sort.key === field ? (sort.direction === 'desc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : null;

  return (
    <div className="page-stack">
      <section className="page-heading compact-page-heading">
        <div>
          <div className="eyebrow"><SlidersHorizontal size={12} /> Evidence-first discovery</div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <button className="subtle-button"><Download size={16} /> Export view</button>
      </section>

      <section className="surface-card screener-card">
        <div className="screener-toolbar">
          <label className="table-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search symbol, company or industry" /></label>
          <label><Filter size={15} /><select aria-label="Filter by sector" value={sector} onChange={(event) => setSector(event.target.value)}>{sectors.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><select aria-label="Filter by movement" value={movement} onChange={(event) => setMovement(event.target.value)}><option>All moves</option><option>Gainers</option><option>Losers</option></select></label>
          <span className="results-count">{filtered.length} results</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Watch</th>
              <th><button onClick={() => changeSort('symbol')}>Company <SortIcon field="symbol" /></button></th>
              <th><button onClick={() => changeSort('price')}>Price <SortIcon field="price" /></button></th>
              <th><button onClick={() => changeSort('change')}>1D <SortIcon field="change" /></button></th>
              <th><button onClick={() => changeSort('marketCap')}>Market cap <SortIcon field="marketCap" /></button></th>
              <th><button onClick={() => changeSort('pe')}>P/E <SortIcon field="pe" /></button></th>
              <th><button onClick={() => changeSort('roe')}>ROE <SortIcon field="roe" /></button></th>
              <th>Volume</th>
              <th>Quality*</th>
            </tr></thead>
            <tbody>
              {filtered.map((stock) => (
                <tr key={stock.symbol} onClick={() => onSelectStock(stock.symbol)}>
                  <td><button className={`watch-button ${watchlist.includes(stock.symbol) ? 'saved' : ''}`} aria-label={`${watchlist.includes(stock.symbol) ? 'Remove' : 'Add'} ${stock.symbol} watchlist`} onClick={(event) => { event.stopPropagation(); onToggleWatchlist(stock.symbol); }}><Bookmark size={16} fill={watchlist.includes(stock.symbol) ? 'currentColor' : 'none'} /></button></td>
                  <td><div className="company-cell"><span className="stock-monogram">{stock.symbol.slice(0, 2)}</span><span><strong>{stock.symbol}</strong><small>{stock.name}</small></span></div></td>
                  <td className="numeric"><strong>{formatInr(stock.price)}</strong></td>
                  <td className={`numeric ${stock.change >= 0 ? 'positive' : 'negative'}`}>{stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}%</td>
                  <td className="numeric">{formatCrores(stock.marketCap)}</td>
                  <td className="numeric">{stock.pe.toFixed(1)}×</td>
                  <td className="numeric">{stock.roe.toFixed(1)}%</td>
                  <td className="numeric">{formatVolume(stock.volume)}</td>
                  <td><span className={`quality-pill ${stock.quality >= 85 ? 'high' : ''}`}>{stock.quality}/100</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">* Quality is a transparent illustrative data composite, not a rating or investment recommendation.</footer>
      </section>
    </div>
  );
}
